import { useEffect, useState } from 'react';
import { X, MessageCircle } from 'lucide-react';

export default function WhatsAppPopup({ lang = 'pt' }) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  const t = {
    pt: {
      badge: '💬 Suporte ao vivo',
      title: 'Olá! Precisa de ajuda? 👋',
      body: 'A nossa equipa está disponível agora mesmo para responder às suas dúvidas sobre aluguer de equipamentos ortopédicos.',
      highlight: '⚡ Resposta em menos de 5 minutos',
      btn: '💬 Falar connosco no WhatsApp',
      close: 'Agora não',
    },
    en: {
      badge: '💬 Live support',
      title: 'Hi there! Need help? 👋',
      body: 'Our team is available right now to answer your questions about orthopedic equipment rental.',
      highlight: '⚡ Response in less than 5 minutes',
      btn: '💬 Chat with us on WhatsApp',
      close: 'Not now',
    }
  };
  const tr = t[lang] || t.pt;

  // Auto-open disabled by user request
  useEffect(() => {
    return () => {};
  }, []);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setProgress(p => Math.max(0, p - (100 / 300)));
    }, 100);
    const closeTimer = setTimeout(() => setVisible(false), 30000);
    return () => {
      clearInterval(interval);
      clearTimeout(closeTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ boxShadow: '0 20px 60px rgba(37,211,102,0.25), 0 4px 20px rgba(0,0,0,0.12)', animation: 'slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(30px) scale(0.92); } to { opacity:1; transform:translateY(0) scale(1); } } @keyframes pulse-green { 0%,100%{box-shadow:0 0 0 0 rgba(37,211,102,0.4);} 50%{box-shadow:0 0 0 8px rgba(37,211,102,0);} }`}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#128C7E] to-[#25D366] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center" style={{ animation: 'pulse-green 2s infinite' }}>
            <MessageCircle className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-white/80 text-xs">{tr.badge}</span>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-300 rounded-full" style={{ animation: 'pulse-green 1.5s infinite' }} />
              <span className="text-white text-xs font-medium">Online</span>
            </div>
          </div>
        </div>
        <button onClick={() => setVisible(false)} aria-label="Fechar" className="p-2 -mr-1 text-white hover:bg-white/15 rounded-lg transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 pt-4 pb-2">
        <p className="text-gray-900 font-bold text-base mb-2">{tr.title}</p>
        <p className="text-gray-500 text-sm leading-relaxed mb-3">{tr.body}</p>
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-green-700 text-xs font-semibold">
          {tr.highlight}
        </div>
      </div>

      {/* Buttons */}
      <div className="px-4 pb-4 pt-3 flex flex-col gap-2">
        <a
          href="https://wa.me/351216035281"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full text-white font-bold py-3 rounded-xl text-sm text-center flex items-center justify-center gap-2 transition-transform hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 4px 15px rgba(37,211,102,0.4)' }}
        >
          {tr.btn}
        </a>
        <button
          onClick={() => setVisible(false)}
          className="w-full text-gray-600 hover:text-gray-800 text-xs py-2 transition-colors"
        >
          {tr.close}
        </button>
      </div>

      {/* Auto-close progress bar */}
      <div className="h-1 bg-gray-100">
        <div
          className="h-full transition-all duration-100 ease-linear"
          style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #128C7E, #25D366)' }}
        />
      </div>
    </div>
  );
}